import { useId } from 'react';
import { Card, CardContent, CardTitle } from '@/shared/components';
import { CircleAlert, CircleCheck, CircleDashed } from 'lucide-react';
import type { ReadinessItem, ReadinessStatus } from '../../utils/publishReadiness';

const STATUS_ICON: Record<ReadinessStatus, { Icon: typeof CircleCheck; className: string; label: string }> = {
  ready: { Icon: CircleCheck, className: 'text-mp-success', label: 'Done' },
  blocked: { Icon: CircleAlert, className: 'text-mp-warning', label: 'Needs action' },
  advisory: { Icon: CircleDashed, className: 'text-mp-muted', label: 'Optional' },
};

export function PublishChecklist({ items }: { items: ReadinessItem[] }) {
  const headingId = useId();
  const isReady = items.every((item) => item.status !== 'blocked');

  return (
    <Card ariaLabelledBy={headingId}>
      <CardContent className="space-y-3 py-5">
        <CardTitle size="sm" id={headingId}>
          {isReady ? 'Ready to publish' : 'Before publishing'}
        </CardTitle>
        <ul className="space-y-2.5 text-sm">
          {items.map(({ id, status, title, detail }) => {
            const { Icon, className, label } = STATUS_ICON[status];
            return (
              <li key={id} className="flex gap-2.5">
                <Icon aria-hidden="true" className={`mt-0.5 size-4 shrink-0 ${className}`} />
                <span>
                  <span className="sr-only">{label}: </span>
                  <span className={status === 'blocked' ? 'font-medium' : undefined}>{title}</span>
                  {detail && <span className="block text-mp-muted">{detail}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
